import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, already
  const [message, setMessage] = useState('');

  const token = searchParams.get('token');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setMessage('No verification token provided.');
      return;
    }

    let cancelled = false;
    const verify = async () => {
      try {
        const res = await axios.get(`${API}/auth/verify-email?token=${encodeURIComponent(token)}`, {
          withCredentials: true,
        });
        if (cancelled) return;
        if (res.data.already_verified) {
          setStatus('already');
          setMessage('Your email is already verified. You can log in now.');
          setTimeout(() => navigate('/admin'), 2500);
        } else {
          setStatus('success');
          setMessage('Your email has been verified successfully!');
          setTimeout(() => navigate('/admin/dashboard'), 2500);
        }
      } catch (err) {
        if (cancelled) return;
        const detail =
          err.response?.data?.detail ||
          'Verification failed. The link may be invalid or expired.';
        setStatus('error');
        setMessage(detail);
      }
    };

    verify();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 to-purple-100 p-4">
      <Card className="w-full max-w-md border-0 shadow-xl shadow-indigo-900/5">
        <CardContent className="pt-8 pb-8 text-center">
          {status === 'verifying' && (
            <>
              <Loader2 className="w-12 h-12 animate-spin text-indigo-600 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Verifying your email...</h2>
              <p className="text-gray-500">Please wait a moment.</p>
            </>
          )}

          {status === 'success' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-indigo-100 rounded-full">
                  <CheckCircle className="w-12 h-12 text-indigo-600" />
                </div>
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-testid="verify-success">
                Email Verified!
              </h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500 mb-4">Redirecting to dashboard...</p>
              <div className="animate-spin w-6 h-6 border-2 border-indigo-600 border-t-transparent rounded-full mx-auto" />
            </>
          )}

          {status === 'already' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-indigo-100 rounded-full">
                  <CheckCircle className="w-12 h-12 text-indigo-600" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">Already Verified</h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <Link to="/admin">
                <Button data-testid="go-to-login">Go to Admin Login</Button>
              </Link>
            </>
          )}

          {status === 'error' && (
            <>
              <div className="flex justify-center mb-4">
                <div className="p-4 bg-red-100 rounded-full">
                  <XCircle className="w-12 h-12 text-red-600" />
                </div>
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2" data-testid="verify-error">
                Verification Failed
              </h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500 mb-4">
                Email apps sometimes open the link automatically. If you just signed up, try logging
                in — your account may already be verified.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Link to="/admin">
                  <Button data-testid="go-to-login">Go to Admin Login</Button>
                </Link>
                <Link to="/admin/signup">
                  <Button variant="outline">Back to Signup</Button>
                </Link>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
